import { useEffect, useRef, useState } from 'react';
import { BorisAvatar } from '../components/BorisAvatar';
import { BorisAnswerFeedback } from '../components/BorisAnswerFeedback';
import { logAnalyticsEvent } from '../../../analytics/nativeAnalytics';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { Button } from '@finn-auto/design-system/atoms/button';
import { Input } from '@finn-auto/design-system/atoms/input';
import { cn } from '../../../shared';
import {
  askBoris,
  BorisRefusalError,
  MAX_QUESTION_CHARS,
  type PolicyChatCitation,
  type PolicyChatTurn,
} from '../services/policyChatApi';
import { usePeopleTicketsAccess } from '../components/peopleTickets/usePeopleTicketsAccess';
import type { SubmitRequestState } from './SubmitRequestView';

interface Message {
  role: 'user' | 'assistant';
  text: string;
  citations?: PolicyChatCitation[];
  /** On an answer: false when the pages did not (fully) answer it. */
  covered?: boolean;
  /** On an answer: the question it answers. */
  question?: string;
  /**
   * On an answer: what "Open a request" hands over — the employee's own questions up to this one,
   * so a follow-up ("What about part-time?") keeps its subject (CodeRabbit, PR #493). Boris's
   * answers are left out: the request is in the employee's words. Separate from `question`, which
   * stays the one question this answer is about.
   */
  handoff?: string;
  /** On a real model answer: what a 👍/👎 names. */
  answerId?: string;
}

/** How many of the employee's questions travel into a request: enough for a follow-up's subject. */
const HANDOFF_QUESTIONS = 3;

const GREETING = 'Hey there 👋 What can I help you with today?';

/**
 * Boris cites facts inline as "[1]", "[2]" (proxy-server/lib/policyChat.js), with
 * `citations[0]` guaranteed to be "[1]"'s source. Rewrites those markers into real
 * markdown links so the reader can click a citation without scrolling to the list
 * below. A bracket with no matching citation (an out-of-range or hallucinated
 * number) is left as plain text rather than linked to nothing.
 */
function linkifyCitations(text: string, citations?: PolicyChatCitation[]): string {
  if (!citations?.length) return text;
  return text.replace(/\[\s*(\d+(?:\s*,\s*\d+)*)\s*\]/g, (_match, nums: string) =>
    nums
      .split(',')
      .map((n) => {
        const citation = citations[Number(n.trim()) - 1];
        if (!citation) return `[${n.trim()}]`;
        const title = citation.title.replace(/"/g, "'");
        return `[[${n.trim()}]](${citation.url} "${title}")`;
      })
      .join(', ')
  );
}

const SUGGESTIONS = [
  'What do I need to know to apply for parental leave?',
  'Do I need to watch out for anything specific when submitting an expense?',
  'Which benefits does FINN offer?',
];

function ChatWithBorisContent() {
  const [messages, setMessages] = useState<Message[]>([{ role: 'assistant', text: GREETING }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  // The chips are a starting point, not a permanent fixture — once the user has asked their own
  // question, the conversation itself is the more useful thing to show in that space.
  const hasAsked = messages.some((m) => m.role === 'user');
  const endRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  // The request form is behind the People Tickets pilot gate; offering it to someone outside the
  // gate would land them on "Coming soon". Outside it, the answer's own advice (ask your People
  // Business Partner) stands.
  const { access } = usePeopleTicketsAccess();
  const canOpenRequest = Boolean(access?.enabled);

  // A new answer lands below the fold once the conversation is longer than the panel — follow it.
  useEffect(() => {
    endRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'end' });
  }, [messages, sending]);

  async function send(text: string) {
    const question = text.trim();
    if (!question || sending) return;
    // Everything said so far, minus the canned greeting, is what lets a follow-up be understood.
    const history: PolicyChatTurn[] = messages
      .filter((m) => m.text !== GREETING)
      .map(({ role, text: t }) => ({ role, text: t }));
    setMessages((m) => [...m, { role: 'user', text: question }]);
    setInput('');
    setSending(true);
    // Counts only, here and below: never the question or the answer (PII rule, CLAUDE.md).
    void logAnalyticsEvent('boris_question_asked', { followUp: history.length > 0 });
    try {
      const { answer, citations, covered, answerId } = await askBoris(question, history);
      // Only a real model answer counts (it carries an answerId). The proxy's not-configured and
      // answer-failed replies come back as ordinary text, and counting them would turn an outage
      // into a drop in coverage (CodeRabbit, PR #494).
      if (answerId) {
        void logAnalyticsEvent('boris_answer_received', {
          covered: covered ?? null,
          sources: citations.length,
        });
      }
      const asked = [...history.filter((t) => t.role === 'user').map((t) => t.text), question];
      const handoff = asked.slice(-HANDOFF_QUESTIONS).join('\n');
      setMessages((m) => [
        ...m,
        { role: 'assistant', text: answer, citations, covered, question, handoff, answerId },
      ]);
    } catch (err) {
      const text =
        err instanceof BorisRefusalError ? err.message : 'Something went wrong — please try again.';
      setMessages((m) => [...m, { role: 'assistant', text }]);
    } finally {
      setSending(false);
    }
  }

  // pb-20 below xl: the floating Feedback button (bottom-right) would otherwise sit on Send.
  return (
    <div className="mx-auto flex h-[calc(100vh-56px)] max-w-2xl flex-col p-6 pb-20 xl:pb-6">
      <div className="mb-4 flex items-center gap-3">
        <BorisAvatar />
        <h1 className="text-xl font-semibold text-gray-900">Chat with Boris</h1>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        {messages.map((m, i) =>
          m.role === 'user' ? (
            <div key={i} className="flex justify-end">
              <div className="max-w-[80%] rounded-2xl rounded-br-sm bg-trustedBlue px-3.5 py-2 text-sm text-white">
                {m.text}
              </div>
            </div>
          ) : (
            <div key={i} className="flex items-start gap-2">
              <BorisAvatar size="sm" />
              <div className="max-w-[80%]">
                <div
                  className={cn(
                    'prose prose-sm max-w-none rounded-2xl rounded-bl-sm bg-gray-100 px-3.5 py-2 text-gray-900',
                    'prose-p:my-1.5 first:prose-p:mt-0 last:prose-p:mb-0',
                    'prose-ul:my-1.5 prose-ol:my-1.5 prose-li:my-0.5',
                    'prose-strong:font-semibold prose-a:text-trustedBlue'
                  )}
                >
                  <ReactMarkdown
                    components={{
                      a: ({ ...props }) => <a {...props} target="_blank" rel="noreferrer" />,
                    }}
                  >
                    {linkifyCitations(m.text, m.citations)}
                  </ReactMarkdown>
                </div>
                {canOpenRequest && m.covered === false && m.handoff && (
                  <div className="mt-2">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        void logAnalyticsEvent('boris_request_opened');
                        navigate('/submit-request', {
                          state: { fromBoris: m.handoff } satisfies SubmitRequestState,
                        });
                      }}
                    >
                      Open a request
                    </Button>
                  </div>
                )}
                {m.citations && m.citations.length > 0 && (
                  <div className="mt-1 space-y-0.5 text-xs text-gray-400">
                    {m.citations.map((c) => (
                      <a key={c.url} href={c.url} target="_blank" rel="noreferrer" className="block hover:underline">
                        {c.title} →
                      </a>
                    ))}
                  </div>
                )}
                {m.answerId && m.question && (
                  <BorisAnswerFeedback
                    answerId={m.answerId}
                    question={m.question}
                    answer={m.text}
                    citations={m.citations ?? []}
                    covered={m.covered}
                  />
                )}
              </div>
            </div>
          )
        )}
        {sending && (
          <div className="flex items-center gap-2">
            <BorisAvatar size="sm" />
            <div className="text-xs text-gray-400">thinking…</div>
          </div>
        )}
        <div ref={endRef} />
      </div>
      {!hasAsked && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {SUGGESTIONS.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => send(s)}
              className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-600 hover:border-trustedBlue hover:text-trustedBlue"
            >
              {s}
            </button>
          ))}
        </div>
      )}
      <div className="mt-3 flex gap-2">
        <div className="flex-1">
          <Input
            placeholder="Ask a policy question…"
            value={input}
            maxLength={MAX_QUESTION_CHARS}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && send(input)}
          />
        </div>
        <Button type="button" variant="primary" onClick={() => send(input)} disabled={sending} loading={sending}>
          Send
        </Button>
      </div>
    </div>
  );
}

/** Open to every employee since Oct 8 2026; the proxy route never checked a permission. */
export default function ChatWithBorisView() {
  return <ChatWithBorisContent />;
}
